import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Alert, CircularProgress, Breadcrumbs, Link, InputAdornment, Divider, IconButton,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import EuroIcon from '@mui/icons-material/Euro';
import GroupsIcon from '@mui/icons-material/Groups';
import RestoreIcon from '@mui/icons-material/Restore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

export const PianiEditPage = () => {
    const { pianoId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    const [formData, setFormData] = React.useState({
        nome: '', gruppoId: '', DescrizioneCompleta: '', maxNumeroSquadre: '',
        maxInfografiche: '', maxPostGg: '', maxPostWeek: '', maxPostMonth: '', imgQuality: '',
        prezzoMensile: '', prezzoAnnuale: '', prezziPersonalizzati: [], durataDisponibile: [], note: '', flVisibile: true, flAttivo: true, watermark: false, ordinamento: ''
    });

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = React.useState({});
    const [originalData, setOriginalData] = React.useState({});
    const [hasChanges, setHasChanges] = React.useState(false);
    const [gruppi, setGruppi] = React.useState([]);

    // State per dialog conferma cancellazione
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [priceToDeleteIndex, setPriceToDeleteIndex] = React.useState(null);

    // Funzioni per gestire prezzi personalizzati
    const handleAddPrezzoPersonalizzato = () => {
        setFormData(prev => ({
            ...prev,
            prezziPersonalizzati: [...prev.prezziPersonalizzati, { mesi: '', prezzo: '', flAttivo: true }]
        }));
    };

    const handleRequestDeletePrezzo = (index) => {
        setPriceToDeleteIndex(index);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDeletePrezzo = () => {
        if (priceToDeleteIndex !== null) {
            setFormData(prev => ({
                ...prev,
                prezziPersonalizzati: prev.prezziPersonalizzati.filter((_, i) => i !== priceToDeleteIndex)
            }));
            setDeleteConfirmOpen(false);
            setPriceToDeleteIndex(null);
        }
    };

    const handlePrezzoPersonalizzatoChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            prezziPersonalizzati: prev.prezziPersonalizzati.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    const loadData = async () => {
        try {
            const jwt = getJwt();
            const [pianoRes, gruppiRes] = await Promise.all([
                axios.get(`${host}/tbl-piani/${pianoId}`, { headers: { 'Authorization': `Bearer ${jwt}` } }),
                axios.get(`${host}/tbl-gruppo`, { headers: { 'Authorization': `Bearer ${jwt}` }, params: { flVisibile: 1, flAttivo: 1, $sort: { nome: 1 } } })
            ]);

            const piano = pianoRes.data;
            const formDataFromPiano = {
                nome: piano.nome || '', gruppoId: piano.gruppoId || '', DescrizioneCompleta: piano.DescrizioneCompleta || '',
                maxNumeroSquadre: piano.maxNumeroSquadre || '', maxInfografiche: piano.maxInfografiche || '',
                maxPostGg: piano.maxPostGg || '', maxPostWeek: piano.maxPostWeek || '', maxPostMonth: piano.maxPostMonth || '', imgQuality: piano.imgQuality || '',
                prezzoMensile: piano.prezzoMensile || '', prezzoAnnuale: piano.prezzoAnnuale || '',
                prezziPersonalizzati: piano.prezziPersonalizzati || [],
                durataDisponibile: piano.durataDisponibile || ['mensile', 'annuale'],
                note: piano.note || '', flVisibile: piano.flVisibile !== undefined ? piano.flVisibile : true,
                flAttivo: piano.flAttivo !== undefined ? piano.flAttivo : true, watermark: piano.watermark !== undefined ? piano.watermark : false, ordinamento: piano.ordinamento || ''
            };

            setFormData(formDataFromPiano);
            setOriginalData(formDataFromPiano);
            setGruppi(gruppiRes.data.data || []);
        } catch (error) {
            notify("Errore nel caricamento", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setTouched(prev => ({ ...prev, [name]: true }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const jwt = getJwt();
            const dataToSend = {
                nome: formData.nome.trim(), gruppoId: formData.gruppoId ? parseInt(formData.gruppoId) : null,
                DescrizioneCompleta: formData.DescrizioneCompleta.trim() || null,
                maxNumeroSquadre: formData.maxNumeroSquadre ? parseInt(formData.maxNumeroSquadre) : 0,
                maxInfografiche: formData.maxInfografiche ? parseInt(formData.maxInfografiche) : 0,
                maxPostGg: formData.maxPostGg ? parseInt(formData.maxPostGg) : 0,
                maxPostWeek: formData.maxPostWeek ? parseInt(formData.maxPostWeek) : 0,
                maxPostMonth: formData.maxPostMonth ? parseInt(formData.maxPostMonth) : 0,
                imgQuality: formData.imgQuality.trim() || null,
                prezzoMensile: formData.prezzoMensile ? parseFloat(formData.prezzoMensile) : null,
                prezzoAnnuale: formData.prezzoAnnuale ? parseFloat(formData.prezzoAnnuale) : null,
                prezziPersonalizzati: formData.prezziPersonalizzati
                    .filter(p => p.mesi && p.prezzo)
                    .map(p => ({
                        mesi: parseInt(p.mesi),
                        prezzo: parseFloat(p.prezzo),
                        flAttivo: p.flAttivo !== undefined ? p.flAttivo : true
                    })),
                durataDisponibile: formData.durataDisponibile,
                note: formData.note.trim() || null, flVisibile: formData.flVisibile ? 1 : 0, flAttivo: formData.flAttivo ? 1 : 0,
                watermark: formData.watermark ? 1 : 0,
                ordinamento: formData.ordinamento ? parseInt(formData.ordinamento) : 0
            };

            await axios.patch(`${host}/tbl-piani/${pianoId}`, dataToSend, { headers: { 'Authorization': `Bearer ${jwt}` } });
            notify(`Piano aggiornato con successo`, { type: 'success' });
            navigate('/piani');
        } catch (error) {
            notify("Errore nell'aggiornamento", { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    React.useEffect(() => { loadData(); }, [pianoId]);
    React.useEffect(() => { setHasChanges(JSON.stringify(formData) !== JSON.stringify(originalData)); }, [formData, originalData]);

    if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link component="button" variant="body2" onClick={() => navigate('/piani')}>Piani</Link>
                    <Typography variant="body2" color="text.primary">Modifica</Typography>
                </Breadcrumbs>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>📦 Modifica Piano</Typography>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/piani')}>Indietro</Button>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Informazioni Piano</Typography>
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Nome Piano *" value={formData.nome} onChange={(e) => handleFieldChange('nome', e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Gruppo</InputLabel>
                                            <Select value={formData.gruppoId} label="Gruppo" onChange={(e) => handleFieldChange('gruppoId', e.target.value)}
                                                startAdornment={<InputAdornment position="start"><GroupsIcon sx={{ color: '#1565c0' }} /></InputAdornment>}>
                                                <MenuItem value="">Nessuno</MenuItem>
                                                {gruppi.map(g => <MenuItem key={g.id} value={g.id}>{g.nome}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField fullWidth label="Prezzo Mensile" type="number" value={formData.prezzoMensile} onChange={(e) => handleFieldChange('prezzoMensile', e.target.value)}
                                            inputProps={{ min: 0, step: 0.01 }} InputProps={{ startAdornment: <InputAdornment position="start"><EuroIcon /></InputAdornment> }} />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField fullWidth label="Prezzo Annuale" type="number" value={formData.prezzoAnnuale} onChange={(e) => handleFieldChange('prezzoAnnuale', e.target.value)}
                                            inputProps={{ min: 0, step: 0.01 }} InputProps={{ startAdornment: <InputAdornment position="start"><EuroIcon /></InputAdornment> }} />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Prezzi Personalizzati (es. 4 mesi, 6 mesi)
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<AddCircleOutlineIcon />}
                                                onClick={handleAddPrezzoPersonalizzato}
                                            >
                                                Aggiungi Prezzo
                                            </Button>
                                        </Box>

                                        {formData.prezziPersonalizzati.map((item, index) => (
                                            <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Numero Mesi"
                                                        type="number"
                                                        value={item.mesi}
                                                        onChange={(e) => handlePrezzoPersonalizzatoChange(index, 'mesi', e.target.value)}
                                                        placeholder="es. 4"
                                                        inputProps={{ min: 1 }}
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Prezzo"
                                                        type="number"
                                                        value={item.prezzo}
                                                        onChange={(e) => handlePrezzoPersonalizzatoChange(index, 'prezzo', e.target.value)}
                                                        placeholder="0.00"
                                                        inputProps={{ min: 0, step: 0.01 }}
                                                        size="small"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <EuroIcon fontSize="small" />
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={item.flAttivo !== false}
                                                                onChange={(e) => handlePrezzoPersonalizzatoChange(index, 'flAttivo', e.target.checked)}
                                                                size="small"
                                                                color="success"
                                                            />
                                                        }
                                                        label={item.flAttivo !== false ? "Attivo" : "Inattivo"}
                                                    />
                                                </Grid>
                                                <Grid item xs={1}>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRequestDeletePrezzo(index)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        ))}

                                        {formData.prezziPersonalizzati.length === 0 && (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                                Nessun prezzo personalizzato. Clicca "Aggiungi Prezzo" per aggiungerne uno.
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Descrizione" multiline rows={3} value={formData.DescrizioneCompleta} onChange={(e) => handleFieldChange('DescrizioneCompleta', e.target.value)} />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField fullWidth label="Max Squadre" type="number" value={formData.maxNumeroSquadre} onChange={(e) => handleFieldChange('maxNumeroSquadre', e.target.value)} inputProps={{ min: 0 }} />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField fullWidth label="Max Infografiche" type="number" value={formData.maxInfografiche} onChange={(e) => handleFieldChange('maxInfografiche', e.target.value)} inputProps={{ min: 0 }} />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField fullWidth label="Max Post/Giorno" type="number" value={formData.maxPostGg} onChange={(e) => handleFieldChange('maxPostGg', e.target.value)} inputProps={{ min: 0 }} />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField fullWidth label="Max Post/Settimana" type="number" value={formData.maxPostWeek} onChange={(e) => handleFieldChange('maxPostWeek', e.target.value)} inputProps={{ min: 0 }} />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <TextField fullWidth label="Max Post/Mese" type="number" value={formData.maxPostMonth} onChange={(e) => handleFieldChange('maxPostMonth', e.target.value)} inputProps={{ min: 0 }} />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Qualità Immagini" value={formData.imgQuality} onChange={(e) => handleFieldChange('imgQuality', e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Ordinamento" type="number" value={formData.ordinamento} onChange={(e) => handleFieldChange('ordinamento', e.target.value)} inputProps={{ min: 0 }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Note" multiline rows={2} value={formData.note} onChange={(e) => handleFieldChange('note', e.target.value)} />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Impostazioni</Typography>
                                <FormControlLabel control={<Switch checked={formData.flVisibile} onChange={(e) => handleFieldChange('flVisibile', e.target.checked)} />} label="Piano visibile" sx={{ mb: 1 }} />
                                <FormControlLabel control={<Switch checked={formData.flAttivo} onChange={(e) => handleFieldChange('flAttivo', e.target.checked)} />} label="Piano attivo" sx={{ mb: 1 }} />
                                <FormControlLabel control={<Switch checked={formData.watermark} onChange={(e) => handleFieldChange('watermark', e.target.checked)} />} label="Watermark" />
                            </CardContent>
                        </Card>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave} disabled={!hasChanges || saving} sx={{ py: 1.5 }}>
                                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
                            </Button>
                            <Button variant="outlined" startIcon={<RestoreIcon />} onClick={() => setFormData(originalData)} disabled={!hasChanges || saving}>
                                Ripristina
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </motion.div>

            {/* Dialog Conferma Cancellazione */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Conferma cancellazione</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sei sicuro di voler cancellare questo prezzo personalizzato?
                        Questa azione è irreversibile. Se vuoi solo nasconderlo, puoi disattivarlo usando lo switch.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Annulla</Button>
                    <Button onClick={handleConfirmDeletePrezzo} color="error" autoFocus>
                        Cancella
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
